import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IResultsGuided } from 'src/app/interfaces/iresults-guided';
import { IPathwayQuizResult } from 'src/app/interfaces/ipathway-quiz-result';
import { IComplianceScoring } from 'src/app/interfaces/icompliance-scoring';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { AppConstants } from 'src/app/app-constants';
import { ImageViewerComponent } from 'src/app/pages/general/image-viewer/image-viewer.component';
import { LocaleService } from 'src/app/services/locale.service';
import { formatList, JAPANESE_LANGUAGE } from 'src/app/utils/japanese-punctuation';

@Component({
  host: {
    class: 'theme-light',
  },
  selector: 'app-pathway-visual-aid-result',
  standalone: false,
  templateUrl: './pathway-visual-aid-result.component.html',
  styleUrls: ['./pathway-visual-aid-result.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'results',
    },
  ],
})
export class PathwayVisualAidResultComponent implements OnChanges {
  @Input() resultReady: boolean = false;
  @Input() translationLoaded: boolean = false;
  @Input() guidedResults: (IResultsGuided | IPathwayQuizResult) | undefined =
    undefined;
  @Input() criteriaScore: number = 0;
  @Input() embeddedStepsToReview: string[] | null | undefined = [];
  @Input() hasPassed: boolean = false;
  @Input() isUserCompliant: boolean | null = true;
  /** False when the call is out of compliance and the setting hides the other feedback. */
  @Input() showFeedback: boolean = true;
  @Input() complianceScoringResult: IComplianceScoring | undefined;
  @Input() complianceOnly: boolean = false;
  @Input() totalPositivePoints: number = 0;
  @Input() totalPoints: number = 0;

  expandedIndices: number[] = [];
  calcTypeRatio5pt0min: string = AppConstants.CALL_RATIO_5PT_0MIN;

  constructor(
    private dialog: MatDialog,
    protected localeService: LocaleService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['guidedResults'] && this.guidedResults) {
      this.toggleAllInitially();
    }
  }

  toggle(index: number): void {
    const idx = this.expandedIndices.indexOf(index);
    if (idx > -1) {
      this.expandedIndices.splice(idx, 1);
    } else {
      this.expandedIndices.push(index);
    }
  }

  isExpandedd(index: number): boolean {
    return this.expandedIndices.includes(index);
  }

  toggleAllInitially(): void {
    this.expandedIndices =
      this.guidedResults?.questions
        .map((q, i) => ({ question: q, index: i }))
        .filter((q) => q.question.practiceCallGuided.negativeContext.length > 0)
        .map((q) => q.index) || [];
  }

  openImageModal(imageSrc: string): void {
    this.dialog.open(ImageViewerComponent, {
      data: { imageSrc },
      panelClass: 'image-dialog',
      maxWidth: '90vw',
      autoFocus: false,
    });
  }

  get practiceScore(): number {
    return this.guidedResults?.practiceCall?.score ?? 0;
  }

  get callScoreCalcType(): string {
    return this.guidedResults?.callScoreCalcType ?? '';
  }

  get showMissedFacts(): boolean {
    return this.guidedResults?.showMissedFacts ?? false;
  }

  get hasMissedQuestions(): boolean {
    const questions = this.guidedResults?.questions || [];
    return questions.some(
      (question) =>
        (question.practiceCallGuided.negativeContext?.length || 0) > 0,
    );
  }

  get embeddedQuizReviewStepNames(): string[] {
    const backendStepNames = (this.embeddedStepsToReview ?? [])
      .map((stepName) => stepName?.trim())
      .filter((stepName): stepName is string => !!stepName);

    if (backendStepNames.length > 0) {
      return [...new Set(backendStepNames)];
    }

    return [];
  }

  get embeddedQuizReviewStepNamesText(): string {
    const names = this.embeddedQuizReviewStepNames;
    if (names.length === 0) {
      return '';
    }

    // Japanese lists use "、" with no conjunction: "A、B、C"
    const lang = this.localeService.getCurrentLanguage();
    if (lang === JAPANESE_LANGUAGE) {
      return formatList(names, lang);
    }

    if (names.length === 1) {
      return names[0];
    }

    if (names.length === 2) {
      return `${names[0]} and ${names[1]}`;
    }

    const allButLast = names.slice(0, -1).join(', ');
    const lastName = names[names.length - 1];
    return `${allButLast} and ${lastName}`;
  }

  get embeddedQuizStepWord(): string {
    return this.embeddedQuizReviewStepNames.length === 1 ? 'step' : 'steps';
  }

  get embeddedQuizStepVerb(): string {
    return this.embeddedQuizReviewStepNames.length === 1 ? 'was' : 'were';
  }

  get embeddedQuizAreaWord(): string {
    return this.embeddedQuizReviewStepNames.length === 1 ? 'area' : 'areas';
  }

  get embeddedQuizSummaryTranslationKey(): string {
    const hasStepNames = this.embeddedQuizReviewStepNames.length > 0;
    if (!hasStepNames) {
      return 'results.QUIZ_SUMMARY_BASE';
    }

    if (!this.hasPassed) {
      return 'results.QUIZ_SUMMARY_FAILED';
    }

    return this.hasMissedQuestions
      ? 'results.QUIZ_SUMMARY_PASSED_WITH_MISSES'
      : 'results.QUIZ_SUMMARY_BASE';
  }
}
